/**
 * Compose an email template from a visual style + a content preset.
 *
 * The 7 "letter-shape" styles each declare a shell HTML with a {{__BODY__}}
 * marker (and optionally {{__HEADLINE__}} for hero/banner styles), plus the
 * CSS used for each body paragraph and the agency signature block. Non-letter
 * styles (Receipt, Stat Spotlight, etc.) do not declare a letter shell — they
 * stay single-piece designs and can only be picked alone.
 */

import { EMAIL_DEFAULTS, EmailTemplateDefault } from "./email";
import { EmailContentPreset } from "./email-content";

const FONT_SANS = `'Inter','Helvetica Neue',Helvetica,Arial,sans-serif`;
const FONT_SERIF = `Georgia,'Times New Roman',serif`;

interface LetterShellDef {
  /** Full HTML wrapper with {{__BODY__}} marker (and optionally
   * {{__HEADLINE__}} where the content's subject can become the banner/hero
   * headline). */
  shell: string;
  /** Inline-style applied to each body paragraph. */
  paragraphCss: string;
  /** Inline-style applied to P.S. paragraphs (defaults to a smaller variant
   * of paragraphCss when not provided). */
  psParagraphCss?: string;
  /** Block rendered after paragraphs+signoff when the content's
   * `includeSignature` is not explicitly false. */
  signatureHtml: string;
}

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// ---------------------------------------------------------------------------
// Letter shells, one per letter-compatible style.
// Style keys match those in defaults/email.ts.
// ---------------------------------------------------------------------------

const plainLetter: LetterShellDef = {
  paragraphCss: `margin:0 0 20px 0;`,
  signatureHtml: `<p style="margin:24px 0 4px 0;">Thanks,<br/>{{ agentName }}</p>
<p style="margin:0;color:#2F3847;font-size:14px;">{{ companyName }} · {{ agentPhone }}</p>`,
  psParagraphCss: `margin:24px 0 0 0;color:#2F3847;font-size:14px;`,
  shell: `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#ffffff;">
  <tr><td align="center" style="padding:48px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="540" style="max-width:540px;width:100%;font-family:${FONT_SANS};color:#2F3847;">
      <tr><td style="font-size:16px;line-height:1.7;">{{__BODY__}}</td></tr>
    </table>
  </td></tr>
</table>`,
};

const indigoBanner: LetterShellDef = {
  paragraphCss: `margin:0 0 16px 0;`,
  signatureHtml: `<p style="margin:24px 0 4px 0;">Thanks,<br/>{{ agentName }}</p>
<p style="margin:0;color:#2F3847;font-size:13px;">{{ companyName }} · {{ agentPhone }}</p>`,
  psParagraphCss: `margin:24px 0 0 0;color:#2F3847;font-size:13px;`,
  shell: `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:32px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;width:100%;background:#ffffff;font-family:${FONT_SANS};">
      <tr><td style="background:#0033A0;padding:28px 32px;">
        <div style="color:#ffffff;font-size:13px;letter-spacing:0.08em;text-transform:uppercase;font-weight:600;opacity:0.85;">{{ companyName }}</div>
        <div style="color:#ffffff;font-size:24px;font-weight:700;line-height:1.3;margin-top:6px;">{{__HEADLINE__}}</div>
      </td></tr>
      <tr><td style="padding:32px;color:#2F3847;font-size:15px;line-height:1.65;">{{__BODY__}}</td></tr>
      <tr><td style="padding:24px 32px;border-top:1px solid #CED7E0;color:#2F3847;font-size:12px;line-height:1.5;">
        {{ companyName }} · {{ agentPhone }} · <a href="mailto:{{ agentEmail }}" style="color:#0033A0;text-decoration:none;">{{ agentEmail }}</a>
      </td></tr>
    </table>
  </td></tr>
</table>`,
};

const centeredCard: LetterShellDef = {
  paragraphCss: `margin:0 0 18px 0;font-size:15px;line-height:1.65;color:#2F3847;`,
  signatureHtml: `<p style="margin:24px 0 4px 0;font-size:15px;color:#0E1941;">Thanks,<br/>{{ agentName }}</p>
<p style="margin:0;color:#2F3847;font-size:13px;">{{ companyName }} · {{ agentPhone }}</p>`,
  psParagraphCss: `margin:24px 0 0 0;color:#2F3847;font-size:13px;line-height:1.55;`,
  shell: `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:56px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="520" style="max-width:520px;width:100%;background:#ffffff;border-radius:14px;font-family:${FONT_SANS};">
      <tr><td style="padding:48px 44px;color:#2F3847;">
        <div style="font-size:11px;letter-spacing:0.16em;text-transform:uppercase;color:#0033A0;font-weight:700;">{{ companyName }}</div>
        <h1 style="margin:14px 0 22px 0;font-size:22px;line-height:1.3;font-weight:700;color:#0E1941;">{{__HEADLINE__}}</h1>
        {{__BODY__}}
      </td></tr>
    </table>
    <div style="margin-top:18px;color:#2F3847;font-size:12px;font-family:${FONT_SANS};">
      {{ companyName }} · {{ agentPhone }}
    </div>
  </td></tr>
</table>`,
};

const boldHero: LetterShellDef = {
  paragraphCss: `margin:0 0 18px 0;`,
  signatureHtml: `<p style="margin:24px 0 4px 0;">Thanks,<br/>{{ agentName }}</p>
<p style="margin:0;color:#2F3847;font-size:13px;">{{ companyName }} · {{ agentPhone }}</p>`,
  psParagraphCss: `margin:24px 0 0 0;color:#2F3847;font-size:13px;line-height:1.55;`,
  shell: `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#ffffff;">
  <tr><td align="center">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;width:100%;font-family:${FONT_SANS};">
      <tr><td style="background:#0E1941;padding:64px 40px 56px 40px;">
        <div style="color:#45BCE5;font-size:12px;letter-spacing:0.18em;text-transform:uppercase;font-weight:600;">{{ companyName }}</div>
        <h1 style="margin:18px 0 0 0;color:#ffffff;font-size:28px;line-height:1.2;font-weight:800;letter-spacing:-0.02em;">{{__HEADLINE__}}</h1>
      </td></tr>
      <tr><td style="padding:40px;color:#2F3847;font-size:16px;line-height:1.65;">{{__BODY__}}</td></tr>
    </table>
  </td></tr>
</table>`,
};

const imageHero: LetterShellDef = {
  paragraphCss: `margin:0 0 16px 0;`,
  signatureHtml: `<p style="margin:24px 0 4px 0;">Thanks,<br/>{{ agentName }}</p>
<p style="margin:0;color:#2F3847;font-size:13px;">{{ companyName }} · {{ agentPhone }}</p>`,
  psParagraphCss: `margin:24px 0 0 0;color:#2F3847;font-size:13px;line-height:1.55;`,
  shell: `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:24px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;width:100%;background:#ffffff;font-family:${FONT_SANS};">
      <tr><td style="height:220px;background:linear-gradient(135deg,#45BCE5 0%,#0033A0 100%);background-color:#45BCE5;text-align:center;vertical-align:middle;color:#0033A0;font-size:14px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;">
        Replace with your image
      </td></tr>
      <tr><td style="padding:32px 40px 12px 40px;">
        <h1 style="margin:0;font-size:24px;line-height:1.25;font-weight:700;color:#0E1941;">{{__HEADLINE__}}</h1>
      </td></tr>
      <tr><td style="padding:8px 40px 32px 40px;color:#2F3847;font-size:15px;line-height:1.65;">{{__BODY__}}</td></tr>
      <tr><td style="padding:18px 40px 28px 40px;border-top:1px solid #CED7E0;color:#2F3847;font-size:12px;">
        {{ companyName }} · {{ agentPhone }}
      </td></tr>
    </table>
  </td></tr>
</table>`,
};

const darkMode: LetterShellDef = {
  paragraphCss: `margin:0 0 18px 0;color:#FFFFFF;`,
  signatureHtml: `<p style="margin:24px 0 4px 0;color:#FFFFFF;">Thanks,<br/>{{ agentName }}</p>
<p style="margin:0;color:#45BCE5;font-size:13px;">{{ companyName }} · {{ agentPhone }}</p>`,
  psParagraphCss: `margin:24px 0 0 0;color:#45BCE5;font-size:13px;line-height:1.55;`,
  shell: `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#0E1941;">
  <tr><td align="center" style="padding:48px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;background:#0E1941;border:1px solid #0E1941;font-family:${FONT_SANS};">
      <tr><td style="padding:36px 36px 0 36px;">
        <div style="font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:#F44025;font-weight:700;">{{ companyName }}</div>
        <h1 style="margin:18px 0 22px 0;font-size:24px;line-height:1.25;font-weight:800;color:#FFFFFF;">{{__HEADLINE__}}</h1>
      </td></tr>
      <tr><td style="padding:0 36px 36px 36px;font-size:15px;line-height:1.7;color:#FFFFFF;">{{__BODY__}}</td></tr>
    </table>
  </td></tr>
</table>`,
};

const editorialSerif: LetterShellDef = {
  paragraphCss: `margin:0 0 18px 0;font-size:17px;line-height:1.7;color:#2F3847;`,
  signatureHtml: `<p style="margin:28px 0 4px 0;font-size:15px;color:#0E1941;font-family:${FONT_SANS};">Thanks,<br/>{{ agentName }}</p>
<p style="margin:0;color:#0033A0;font-size:12px;letter-spacing:0.06em;text-transform:uppercase;font-family:${FONT_SANS};">{{ companyName }} · {{ agentPhone }}</p>`,
  psParagraphCss: `margin:24px 0 0 0;font-size:14px;line-height:1.6;color:#2F3847;font-family:${FONT_SANS};`,
  shell: `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:56px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;font-family:${FONT_SERIF};">
      <tr><td style="text-align:center;font-family:${FONT_SANS};font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#0033A0;font-weight:700;padding-bottom:20px;">
        {{ companyName }} · Notes
      </td></tr>
      <tr><td style="text-align:center;padding-bottom:28px;">
        <h1 style="margin:0;font-size:30px;line-height:1.2;font-weight:700;color:#0E1941;letter-spacing:-0.01em;">{{__HEADLINE__}}</h1>
      </td></tr>
      <tr><td style="padding:0 16px;">{{__BODY__}}</td></tr>
    </table>
  </td></tr>
</table>`,
};

const agentNote: LetterShellDef = {
  paragraphCss: `margin:0 0 18px 0;`,
  signatureHtml: `<p style="margin:24px 0 0 0;font-family:${FONT_SERIF};font-size:38px;font-style:italic;color:#0033A0;line-height:1.1;letter-spacing:-0.01em;">{{ agentName }}</p>`,
  psParagraphCss: `margin:24px 0 0 0;color:#2F3847;font-size:13px;line-height:1.55;`,
  shell: `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#E4E9ED;">
  <tr><td align="center" style="padding:48px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;background:#FFFFFF;font-family:${FONT_SANS};">
      <tr><td style="padding:32px 36px 0 36px;text-align:center;">
        <div style="font-family:${FONT_SERIF};font-size:14px;font-style:italic;color:#0033A0;letter-spacing:0.04em;">a personal message</div>
      </td></tr>
      <tr><td style="padding:24px 36px 0 36px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#0033A0;">
          <tr>
            <td width="106" valign="top" style="padding:18px 0 18px 18px;">
              <div style="width:80px;height:80px;background:#FFFFFF;border-radius:8px;text-align:center;vertical-align:middle;color:#0033A0;font-size:28px;font-weight:800;line-height:80px;letter-spacing:-0.02em;">AN</div>
            </td>
            <td valign="middle" style="padding:18px;">
              <div style="font-size:19px;font-weight:800;color:#FFFFFF;letter-spacing:-0.01em;">{{ agentName }}</div>
              <div style="font-size:11px;color:#45BCE5;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;margin-top:4px;">Your dedicated agent</div>
              <div style="font-size:13px;color:#FFFFFF;margin-top:10px;line-height:1.55;">
                {{ companyName }}<br/>
                {{ agentPhone }}
              </div>
            </td>
          </tr>
        </table>
      </td></tr>
      <tr><td style="padding:32px 36px;color:#2F3847;font-size:16px;line-height:1.75;">{{__BODY__}}</td></tr>
      <tr><td style="padding:0 36px 32px 36px;border-top:1px solid #CED7E0;padding-top:18px;color:#2F3847;font-size:12px;line-height:1.6;">
        Reach me Mon-Fri, 9-5. I read every reply myself.
      </td></tr>
    </table>
  </td></tr>
</table>`,
};

const LETTER_SHELLS: Record<string, LetterShellDef> = {
  "design.plain-letter": plainLetter,
  "design.indigo-banner": indigoBanner,
  "design.centered-card": centeredCard,
  "design.bold-hero": boldHero,
  "design.image-hero": imageHero,
  "design.dark-mode": darkMode,
  "design.editorial-serif": editorialSerif,
  "design.agent-note": agentNote,
};

export const LETTER_COMPATIBLE_STYLE_KEYS = Object.keys(LETTER_SHELLS);

export const isLetterCompatible = (styleKey: string): boolean =>
  styleKey in LETTER_SHELLS;

// ---------------------------------------------------------------------------
// Body composition.
// ---------------------------------------------------------------------------

const buildBody = (
  content: EmailContentPreset,
  shell: LetterShellDef
): string => {
  const parts: string[] = [];
  const para = (text: string) => `<p style="${shell.paragraphCss}">${text}</p>`;
  const psPara = (text: string) =>
    `<p style="${shell.psParagraphCss ?? shell.paragraphCss}">${text}</p>`;

  if (content.greeting) {
    // Greetings universally reference {{ firstName }} (e.g. "{{ firstName }},",
    // "Dear {{ firstName }},"). When the customer has no firstName attribute
    // the Liquid tag resolves to empty, leaving a stray "," or "Dear ," on its
    // own line. Wrap the greeting paragraph in a Liquid `if` so the entire
    // <p> drops out at send time when firstName is missing. Greetings that
    // don't reference firstName fall through and render unconditionally.
    if (/\bfirstName\b/.test(content.greeting)) {
      parts.push(`{% if firstName %}${para(content.greeting)}{% endif %}`);
    } else {
      parts.push(para(content.greeting));
    }
  }
  for (const p of content.paragraphs) parts.push(para(p));
  if (content.signoff) parts.push(para(content.signoff));
  if (content.includeSignature !== false) parts.push(shell.signatureHtml);
  if (content.ps?.length) {
    for (const psLine of content.ps) parts.push(psPara(`P.S. ${psLine}`));
  }

  return parts.join("\n");
};

// ---------------------------------------------------------------------------
// Public API.
// ---------------------------------------------------------------------------

export interface ComposedEmail {
  subject: string;
  text: string;
  style?: string;
}

/**
 * Compose a content preset onto a style. Returns null when the style isn't
 * letter-compatible (callers should fall back to the style's own default text
 * or block the combination).
 */
export const composeEmail = (
  style: EmailTemplateDefault,
  content: EmailContentPreset
): ComposedEmail | null => {
  const shell = LETTER_SHELLS[style.key];
  if (!shell) return null;

  const body = buildBody(content, shell);
  const headline = escapeHtml(content.subject);
  const text = shell.shell
    .replace("{{__BODY__}}", body)
    .replace("{{__HEADLINE__}}", headline);

  return {
    subject: content.subject,
    text,
    style: style.style,
  };
};

/** Convenience for "content selected but no style" — falls back to Plain Letter. */
export const composeWithPlainLetter = (
  content: EmailContentPreset
): ComposedEmail => {
  const plain = EMAIL_DEFAULTS.find((d) => d.key === "design.plain-letter")!;
  return composeEmail(plain, content)!;
};
